import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckSquare,
  Heading1,
  Lock,
  Minus,
  Plus,
  Trash2,
  Type,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api, type ProjectPageBlock, type ProjectPageBlockType } from '../../lib/api'
import { notify } from '../../lib/notify'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { confirm } from '../ui/confirm-dialog'

const BLOCK_MENU: { type: ProjectPageBlockType; label: string; icon: typeof Type }[] = [
  { type: 'paragraph', label: 'Text', icon: Type },
  { type: 'heading', label: 'Überschrift', icon: Heading1 },
  { type: 'todo', label: 'To-do', icon: CheckSquare },
  { type: 'callout', label: 'Hinweis', icon: AlertCircle },
  { type: 'divider', label: 'Trenner', icon: Minus },
]

/** Wachsende Textarea ohne Scrollbalken — Notion-Gefühl light. */
function AutoTextarea({
  value,
  onChange,
  placeholder,
  className,
  readOnly,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
  readOnly?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const resize = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: Höhe folgt dem Inhalt
  useEffect(resize, [value])
  // Breitenänderung (Rotation, Split-Resize) vermisst die Höhe neu —
  // sonst bleibt Text geklippt (Review-Fix).
  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only-Observer, resize greift nur auf ref zu
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(resize)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return (
    <textarea
      ref={ref}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value.slice(0, 4000))}
      placeholder={placeholder}
      rows={1}
      className={cn(
        'w-full resize-none overflow-hidden bg-transparent focus:outline-none placeholder:text-text-tertiary/70',
        className,
      )}
    />
  )
}

/**
 * Schlanker Block-Editor für Projekt-Seiten (Hub Paket 6). Bewusst ohne
 * Editor-Framework: begrenzte Block-Typen, Buttons statt Drag&Drop
 * (touch-first), debounced Autosave + expliziter Speichern-Button.
 */
export function PageEditor({
  projectId,
  pageId,
  canEdit,
  onDeleted,
}: {
  projectId: string
  pageId: string
  canEdit: boolean
  onDeleted: () => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['project-page', projectId, pageId],
    queryFn: () => api.getProjectPage(projectId, pageId),
  })

  const [title, setTitle] = useState('')
  const [blocks, setBlocks] = useState<ProjectPageBlock[]>([])
  const [dirty, setDirty] = useState(false)
  // Änderungs-Generation: onSuccess räumt dirty nur, wenn seit Save-Start
  // nichts mehr editiert wurde — sonst gingen Eingaben während eines
  // laufenden Saves still verloren (Review-Fix).
  const editGen = useRef(0)
  // Server-updatedAt-Stand für optimistisches Locking (409 statt LWW).
  const baseUpdatedAt = useRef<string | null>(null)
  // Server-Stand nur beim Seitenwechsel/Erstladen übernehmen — nicht bei
  // jedem Refetch (würde ungespeicherte Eingaben überschreiben).
  const loadedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!data) return
    if (loadedFor.current === pageId) return
    loadedFor.current = pageId
    setTitle(data.page.title)
    setBlocks(data.page.blocks ?? [])
    baseUpdatedAt.current = data.page.updatedAt
    setDirty(false)
  }, [data, pageId])

  const save = useMutation({
    mutationFn: () =>
      api.updateProjectPage(projectId, pageId, {
        // Leerer Titel würde jeden Save (auch Block-Änderungen) mit 400
        // scheitern lassen — Fallback statt Dauerfehler (Review-Fix).
        title: title.trim() || 'Unbenannt',
        blocks,
        baseUpdatedAt: baseUpdatedAt.current ?? undefined,
      }),
    onMutate: () => ({ gen: editGen.current }),
    onSuccess: (r, _v, ctx) => {
      baseUpdatedAt.current = r.page.updatedAt
      if (editGen.current === ctx.gen) setDirty(false)
      qc.setQueryData(['project-page', projectId, pageId], r)
      qc.invalidateQueries({ queryKey: ['project-pages', projectId] })
    },
    onError: (err: Error & { status?: number }) => {
      if (err.status === 409) {
        notify.error('Konflikt: Seite wurde parallel geändert — lade neu.')
        // Server-Stand neu übernehmen (verwirft die lokale Kollision).
        loadedFor.current = null
        qc.invalidateQueries({ queryKey: ['project-page', projectId, pageId] })
        setDirty(false)
        return
      }
      notify.error(err.message)
    },
  })
  const saveRef = useRef(save)
  saveRef.current = save

  // Unmount-Flush: ungespeicherte Änderungen nicht still verwerfen
  // (Review-Fix). Fire-and-forget — die Komponente ist danach weg.
  const flushRef = useRef({ dirty: false, title: '', blocks: [] as ProjectPageBlock[] })
  flushRef.current = { dirty, title, blocks }
  // biome-ignore lint/correctness/useExhaustiveDependencies: nur beim Unmount, aktuelle Werte via flushRef
  useEffect(() => {
    return () => {
      const f = flushRef.current
      if (f.dirty && canEdit) {
        api
          .updateProjectPage(projectId, pageId, {
            title: f.title.trim() || 'Unbenannt',
            blocks: f.blocks,
            baseUpdatedAt: baseUpdatedAt.current ?? undefined,
          })
          .catch(() => {})
      }
    }
  }, [])

  // Debounced Autosave: 1,5s nach der letzten Änderung.
  // biome-ignore lint/correctness/useExhaustiveDependencies: title/blocks starten den Debounce-Timer je Änderung neu
  useEffect(() => {
    if (!dirty || !canEdit) return
    const t = setTimeout(() => {
      if (!saveRef.current.isPending) saveRef.current.mutate()
    }, 1500)
    return () => clearTimeout(t)
  }, [dirty, canEdit, title, blocks])

  const remove = useMutation({
    mutationFn: () => api.deleteProjectPage(projectId, pageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-pages', projectId] })
      notify.success('Seite gelöscht')
      onDeleted()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const mutate = (fn: (prev: ProjectPageBlock[]) => ProjectPageBlock[]) => {
    editGen.current++
    setBlocks(fn)
    setDirty(true)
  }
  const updateBlock = (id: string, patch: Partial<ProjectPageBlock>) =>
    mutate((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)))
  const removeBlock = (id: string) => mutate((prev) => prev.filter((b) => b.id !== id))
  const moveBlock = (id: string, dir: -1 | 1) =>
    mutate((prev) => {
      const i = prev.findIndex((b) => b.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= prev.length) return prev
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  const addBlock = (type: ProjectPageBlockType, afterId?: string) =>
    mutate((prev) => {
      const block: ProjectPageBlock = {
        id: crypto.randomUUID(),
        type,
        ...(type === 'divider' ? {} : { text: '' }),
        ...(type === 'todo' ? { checked: false } : {}),
        ...(type === 'heading' ? { level: 1 as const } : {}),
      }
      if (!afterId) return [...prev, block]
      const i = prev.findIndex((b) => b.id === afterId)
      const next = [...prev]
      next.splice(i + 1, 0, block)
      return next
    })

  if (isLoading || !data) {
    return <p className="py-8 text-center text-sm text-text-tertiary">Lade Seite …</p>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <AutoTextarea
          value={title}
          readOnly={!canEdit}
          onChange={(v) => {
            editGen.current++
            setTitle(v.replace(/\n/g, '').slice(0, 160))
            setDirty(true)
          }}
          placeholder="Seitentitel"
          className="text-lg font-semibold text-text-primary"
        />
        <div className="flex items-center gap-1 shrink-0">
          {!canEdit && (
            <span className="inline-flex items-center gap-1 rounded-full bg-bg-overlay px-2 py-0.5 text-[11px] text-text-tertiary">
              <Lock className="h-3 w-3" /> Nur Lesen
            </span>
          )}
          {canEdit && (
            <>
              <span className="text-[11px] text-text-tertiary tabular-nums w-20 text-right">
                {save.isPending ? 'Speichere …' : dirty ? 'Ungespeichert' : 'Gespeichert'}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}
              >
                Speichern
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={async () => {
                  const ok = await confirm({
                    title: `„${title.trim() || 'Unbenannt'}“ löschen?`,
                    description: 'Alle Blöcke dieser Seite gehen verloren.',
                    variant: 'danger',
                    confirmLabel: 'Löschen',
                  })
                  if (ok) remove.mutate()
                }}
                aria-label="Seite löschen"
                title="Seite löschen"
                className="text-text-tertiary hover:text-danger"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-1">
        {blocks.length === 0 && (
          <p className="text-sm text-text-tertiary py-2">
            {canEdit ? 'Leere Seite — füg unten den ersten Block hinzu.' : 'Leere Seite.'}
          </p>
        )}
        {blocks.map((b, i) => (
          <div
            key={b.id}
            className="group flex items-start gap-1.5 rounded-lg px-1.5 py-0.5 hover:bg-bg-overlay/30"
          >
            <div className="flex-1 min-w-0 pt-0.5">
              {b.type === 'paragraph' && (
                <AutoTextarea
                  value={b.text ?? ''}
                  readOnly={!canEdit}
                  onChange={(v) => updateBlock(b.id, { text: v })}
                  placeholder="Text …"
                  className="text-sm text-text-secondary"
                />
              )}
              {b.type === 'heading' && (
                <AutoTextarea
                  value={b.text ?? ''}
                  readOnly={!canEdit}
                  onChange={(v) => updateBlock(b.id, { text: v.replace(/\n/g, '') })}
                  placeholder="Überschrift …"
                  className={cn(
                    'font-semibold text-text-primary',
                    b.level === 2 ? 'text-base' : 'text-lg',
                  )}
                />
              )}
              {b.type === 'todo' && (
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={b.checked ?? false}
                    disabled={!canEdit}
                    onChange={(e) => updateBlock(b.id, { checked: e.target.checked })}
                    className="accent-accent h-4 w-4 mt-0.5 shrink-0"
                  />
                  <AutoTextarea
                    value={b.text ?? ''}
                    readOnly={!canEdit}
                    onChange={(v) => updateBlock(b.id, { text: v })}
                    placeholder="To-do …"
                    className={cn(
                      'text-sm',
                      b.checked ? 'text-text-tertiary line-through' : 'text-text-secondary',
                    )}
                  />
                </label>
              )}
              {b.type === 'divider' && <hr className="border-border-subtle my-2" />}
              {b.type === 'callout' && (
                <div className="rounded-lg bg-accent/10 border border-accent/20 px-3 py-2">
                  <AutoTextarea
                    value={b.text ?? ''}
                    readOnly={!canEdit}
                    onChange={(v) => updateBlock(b.id, { text: v })}
                    placeholder="Hinweis …"
                    className="text-sm text-text-primary"
                  />
                </div>
              )}
            </div>
            {canEdit && (
              <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
                {b.type === 'heading' && (
                  <button
                    type="button"
                    onClick={() => updateBlock(b.id, { level: b.level === 2 ? 1 : 2 })}
                    title={b.level === 2 ? 'Große Überschrift' : 'Kleine Überschrift'}
                    className="h-6 px-1 rounded text-[10px] font-semibold text-text-tertiary hover:text-accent"
                  >
                    H{b.level === 2 ? 2 : 1}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => moveBlock(b.id, -1)}
                  disabled={i === 0}
                  aria-label="Block nach oben"
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-text-tertiary hover:text-accent disabled:opacity-30"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveBlock(b.id, 1)}
                  disabled={i === blocks.length - 1}
                  aria-label="Block nach unten"
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-text-tertiary hover:text-accent disabled:opacity-30"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeBlock(b.id)}
                  aria-label="Block löschen"
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-text-tertiary hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && blocks.length < 200 && (
        <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-border-subtle">
          <Plus className="h-3.5 w-3.5 text-text-tertiary" />
          {BLOCK_MENU.map(({ type, label, icon: BIcon }) => (
            <button
              key={type}
              type="button"
              onClick={() => addBlock(type)}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
            >
              <BIcon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
